<template>
  <modal
    v-model="isModalOpen"
    :header="false"
    :footer="false"
    size="md"
    :append-to-body="true"
    @hide="closeModal"
    class="visibility-modal">
    <div class="visibility-settings-modal">
      <h3 class="modal-title">Visibility Settings</h3>
      <p class="modal-description">Toggle different visibility features for development and testing.</p>

      <div class="settings-section">
        <div
          v-for="setting in settings"
          :key="setting.id"
          class="setting-item">
          <div class="setting-info">
            <label :for="setting.id" class="setting-label">{{ setting.label }}</label>
            <p class="setting-description">{{ setting.description }}</p>
          </div>
          <input
            :id="setting.id"
            type="checkbox"
            :checked="setting.checked"
            :disabled="setting.disabled"
            @change="setting.onChange($event.target.checked)"
            class="setting-checkbox">
        </div>
      </div>

      <div class="modal-footer">
        <button @click="closeModal" class="btn btn-primary">Close</button>
      </div>
    </div>
  </modal>
</template>

<script>
import { mapState, mapGetters, mapMutations } from 'vuex'

export default {
  name: 'VisibilitySettingsModal',
  props: {
    show: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isModalOpen: this.show
    }
  },
  watch: {
    show(newVal) {
      this.isModalOpen = newVal
    }
  },
  computed: {
    ...mapGetters('visibility', ['betaVisibility', 'editorVisibility', 'iodVisibility', 'effectsVisibility', 'shareVisibility', 'threadsVisibility', 'orientationMobilityVisibility']),
    ...mapGetters('user', ['isSuperAdmin']),
    settings() {
      return [
        {
          id: 'betaModeCheckbox',
          label: 'Beta Mode',
          description: 'Enable beta features',
          checked: this.betaVisibility,
          disabled: false,
          onChange: this.toggleBeta
        },
        {
          id: 'editorModeCheckbox',
          label: 'Editor Mode',
          description: 'Enable editor features (admin only)',
          checked: this.editorVisibility,
          disabled: !this.isSuperAdmin,
          onChange: this.toggleEditor
        },
        {
          id: 'iodModeCheckbox',
          label: 'IOD Visibility',
          description: 'Override IOD feature visibility',
          checked: this.iodVisibility,
          disabled: false,
          onChange: this.toggleIod
        },
        {
          id: 'effectsModeCheckbox',
          label: 'Effects Visibility',
          description: 'Override effects statements visibility',
          checked: this.effectsVisibility,
          disabled: false,
          onChange: this.toggleEffects
        },
        {
          id: 'shareModeCheckbox',
          label: 'Share Visibility',
          description: 'Override share feature visibility',
          checked: this.shareVisibility,
          disabled: false,
          onChange: this.toggleShare
        },
        {
          id: 'threadsModeCheckbox',
          label: 'Threads Visibility',
          description: 'Override threads feature visibility',
          checked: this.threadsVisibility,
          disabled: false,
          onChange: this.toggleThreads
        },
        {
          id: 'orientationMobilityModeCheckbox',
          label: 'Orientation & Mobility Visibility',
          description: 'Show Orientation & Mobility category in Browse Assessments',
          checked: this.orientationMobilityVisibility,
          disabled: false,
          onChange: this.toggleOrientationMobility
        }
      ]
    }
  },
  methods: {
    ...mapMutations('visibility', ['toggleBetaMode', 'toggleEditorMode', 'toggleIodMode', 'toggleEffectsMode', 'toggleShareMode', 'toggleThreadsMode', 'toggleOrientationMobilityMode']),
    toggleBeta(value) {
      this.toggleBetaMode(value)
    },
    toggleEditor(value) {
      this.toggleEditorMode(value)
    },
    toggleIod(value) {
      this.toggleIodMode(value)
    },
    toggleEffects(value) {
      this.toggleEffectsMode(value)
    },
    toggleShare(value) {
      this.toggleShareMode(value)
    },
    toggleThreads(value) {
      this.toggleThreadsMode(value)
    },
    toggleOrientationMobility(value) {
      this.toggleOrientationMobilityMode(value)
    },
    closeModal() {
      this.isModalOpen = false
      this.$emit('close')
    }
  }
}
</script>

<style lang="less">
// Ensure this specific modal appears above backdrop
.visibility-modal.modal.in .modal-dialog {
  z-index: 1051 !important;
}

.visibility-settings-modal {
  padding: 20px;
}


.modal-title {
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 8px;
}

.modal-description {
  color: #666;
  margin-bottom: 24px;
}

.settings-section {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 24px;
}

.setting-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 0;
  border-bottom: 1px solid #eee;
}

.setting-item:last-child {
  border-bottom: none;
}

.setting-info {
  flex: 1;
}

.setting-label {
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  display: block;
  margin-bottom: 4px;
}

.setting-description {
  font-size: 12px;
  color: #666;
  margin: 0;
}

.setting-checkbox {
  width: 20px;
  height: 20px;
  cursor: pointer;
}

.visibility-states {
  border-top: 1px solid #ddd;
  padding-top: 16px;
  margin-bottom: 24px;
}

.states-title {
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 8px;
}

.state-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.state-item {
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  color: #666;
}

.state-item .active {
  color: #28a745;
  font-weight: 500;
}

.state-item .inactive {
  color: #999;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  padding-top: 16px;
  border-top: 1px solid #eee;
}
</style>
